
import util from '../lib/util';
import newHRTimestamp from '../lib/picos/timestamp';
import type {uuid, SchemaField, EntitySchema} from "../@types/schema";
import Base from "./base";
import {genUUID} from "../lib/picos/uuids";
import type {EntityType} from "../@types/entity";

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}
 /**
 * Lookup
 *
 * A centralized catalog of predefined pick-list options used throughout the application. Each row is one selectable option identified by a hierarchical colon-separated path (e.g. 'milestone:reasons:won') and a human-readable key (e.g. 'Personal referral'). Other entities store arrays of lookupId UUIDs to record which options were selected; some entries carry additionalData for extra context such as relationship type or bidirectional opposite-relationship links.
 */
class Lookup extends Base implements EntityType {
    lookupId: uuid;
    path: string;
    key: string;
    hasAdditionalData: boolean;
    additionalData?: any | null;

    /**
     * Create a new Lookup, with an (optional) JSON object or Lookup class
     *
     * @param {Object|Lookup} doc - Can be either a simple JSON object or a Lookup class used to initialize this object's fields
     * @constructor
     */
    constructor(doc: typeof Lookup | any) {
        super(doc);
        
        // @ts-ignore
        if (!doc) doc = {
            firmId: getCurrentFirmId(),
            addedBy: getCurrentUserId(),
            addedOn: newHRTimestamp(),
            status: 'C',
            entityId: null,
            entityType: null,
            lookupId: genUUID(),
            path: null,
            key: null,
        };
        
        // @ts-ignore
        this.entityId = doc._entityId || doc.entityId || doc.entityid || '00000000-0000-0000-0000-000000000000';
        // @ts-ignore
        this.entityType = doc._entityType || doc.entityType || doc.entitytype || 'lookup';
        // @ts-ignore
        this.lookupId = doc._lookupId || doc.lookupId || doc.lookupid || genUUID();
        // @ts-ignore
        this.path = doc._path || doc.path || null;
        // @ts-ignore
        this.key = doc._key || doc.key || null;
        // @ts-ignore
        this.hasAdditionalData = util.defaultBoolean([doc.hasAdditionalData, doc._hasAdditionalData, doc.hasadditionaldata, false]);
        // @ts-ignore
        this.additionalData = doc._additionalData || doc.additionalData || doc.additionaldata || {};

    }
    /**
     * @returns {uuid} This is a generic pointer to the lookupId across all entity types
     */
    get id(): uuid {
        return this.lookupId;
    }


    /**
     * Get a list of pk fields (primary keys)
     *
     * @returns {SchemaField[]}
     */
    static override getPkFields(): SchemaField[] {
        return [{"type":"uuid","required":true,"pk":true,"default":"__UUID__","comment":"Unique id for this combination of path and key"},{"type":"uuid","required":true,"pk":true,"base":true,"default":"__FIRM__","mergeStrategy":"immutable","comment":"Associated firm"}];
    }
    /**
     * TODO: Added just to handle react-select issues.  Can remove when remedied.
     */
    toLowerCase() {
        return this.lookupId;
    }
    
    /**
     * Get a list of non-reference array fields in the schema
     *
     * @return {Array.<{field: string, entity: string}>}
     */
    static override getNonRefArrayFields(): string[] {
        return [];
    }

    /**
     * Default entries are Outlaw-supplied Lookup entries
     * @static
     * @return {object[]}
     */
    static override getDefaultEntries(): Lookup[] {
        return [
        {
                "lookupId": "995f186b-a69a-48ea-bba4-9344af4be7a9",
                "path": "note:tag",
                "key": "import",
                "status": "C"
        },
        {
                "lookupId": "442ada96-c371-4701-a021-1a82d619c066",
                "path": "any:participant:relationship",
                "key": "Client",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Legal"
                },
                "status": "C"
        },
        {
                "lookupId": "73d54d07-2016-4a9e-9c89-ad843d93e178",
                "path": "any:participant:relationship",
                "key": "Benefactor",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Legal"
                },
                "status": "C"
        },
        {
                "lookupId": "fa92071d-cf6a-4208-9082-b0a0ca03d85d",
                "path": "any:participant:relationship",
                "key": "Assigned Attorney",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Legal"
                },
                "status": "C"
        },
        {
                "lookupId": "167b6b1b-9155-4b3c-8063-9a31d8c6c6b3",
                "path": "any:participant:relationship",
                "key": "Opposing Counsel",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Legal"
                },
                "status": "C"
        },
        {
                "lookupId": "53eb1870-58b4-4f4f-a592-9672d940420b",
                "path": "any:participant:relationship",
                "key": "Opposing Party",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Legal"
                },
                "status": "C"
        },
        {
                "lookupId": "7a3c50fb-4f96-4741-ad36-363151352422",
                "path": "any:participant:relationship",
                "key": "Judge",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Legal"
                },
                "status": "C"
        },
        {
                "lookupId": "27ae2710-6fe2-4865-86d4-b571a6be522d",
                "path": "any:participant:relationship",
                "key": "Attorney ad Litem",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Legal"
                },
                "status": "C"
        },
        {
                "lookupId": "9486b60e-c49d-421e-87dd-cd942f1adf24",
                "path": "any:participant:relationship",
                "key": "Guardian ad Litem",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Legal"
                },
                "status": "C"
        },
        {
                "lookupId": "4d03aef5-546f-4751-99ca-0f8f9c8ced3a",
                "path": "any:participant:relationship",
                "key": "Prosecutor",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Legal"
                },
                "status": "C"
        },
        {
                "lookupId": "539e749e-be1c-420e-97e7-58aad5ca38dc",
                "path": "any:participant:relationship",
                "key": "Public Defender",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Legal"
                },
                "status": "C"
        },
        {
                "lookupId": "b61d5053-cc57-4325-a670-eec018da935b",
                "path": "any:participant:relationship",
                "key": "Plaintiff",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Legal"
                },
                "status": "C"
        },
        {
                "lookupId": "f8aa6e11-70f4-4853-8726-77e62eb22129",
                "path": "any:participant:relationship",
                "key": "Defendant",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Legal"
                },
                "status": "C"
        },
        {
                "lookupId": "d69c4e00-7420-4ab5-b646-7b424efa83c5",
                "path": "any:participant:relationship",
                "key": "Petitioner",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Legal"
                },
                "status": "C"
        },
        {
                "lookupId": "b7c863ea-9570-4872-bbf0-c46ecfa23fad",
                "path": "any:participant:relationship",
                "key": "Respondent",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Legal"
                },
                "status": "C"
        },
        {
                "lookupId": "c4a755e8-d92a-45b3-a405-858157f0ebfb",
                "path": "any:participant:relationship",
                "key": "Administrator",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Legal"
                },
                "status": "C"
        },
        {
                "lookupId": "5aaf9d59-d315-4346-959a-a0708d2e0c36",
                "path": "any:participant:relationship",
                "key": "Executor",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Legal"
                },
                "status": "C"
        },
        {
                "lookupId": "af0badc4-cce2-4b31-b572-fb109e5169b1",
                "path": "any:participant:relationship",
                "key": "Appellant",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Legal"
                },
                "status": "C"
        },
        {
                "lookupId": "6d5faffc-594e-4bfc-8144-eeab6c5ae22c",
                "path": "any:participant:relationship",
                "key": "Appellee",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Legal"
                },
                "status": "C"
        },
        {
                "lookupId": "681cd51f-120e-4d38-9238-ceb84a4e5daf",
                "path": "any:participant:relationship",
                "key": "Court Clerk",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Legal"
                },
                "status": "C"
        },
        {
                "lookupId": "72b5c97a-bab7-4c94-b766-e24394e0b335",
                "path": "any:participant:relationship",
                "key": "Court Reporter",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Legal"
                },
                "status": "C"
        },
        {
                "lookupId": "0fbb0e71-bac3-4bc5-a91f-b97b027deed6",
                "path": "any:participant:relationship",
                "key": "Custodian",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Legal"
                },
                "status": "C"
        },
        {
                "lookupId": "e82e240b-ac9d-4921-a7cc-ae5ecb7d2eb3",
                "path": "any:participant:relationship",
                "key": "Guardian",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Legal"
                },
                "status": "C"
        },
        {
                "lookupId": "da0d76fe-d117-4174-a74e-9747fcf17f2b",
                "path": "any:participant:relationship",
                "key": "Decedent",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Legal"
                },
                "status": "C"
        },
        {
                "lookupId": "23bf7b3e-2d93-4d17-8865-b53a16cbb76d",
                "path": "any:participant:relationship",
                "key": "Grantee",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Legal"
                },
                "status": "C"
        },
        {
                "lookupId": "ccfef2fc-36a8-4902-b122-2eb4ae97b69a",
                "path": "any:participant:relationship",
                "key": "Grantor",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Legal"
                },
                "status": "C"
        },
        {
                "lookupId": "1043b48d-b767-49be-831c-41e5978a7a55",
                "path": "any:participant:relationship",
                "key": "Landlord",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Legal"
                },
                "status": "C"
        },
        {
                "lookupId": "cb0f285e-661a-4645-972d-70ccd695bdb8",
                "path": "any:participant:relationship",
                "key": "Tenant",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Legal"
                },
                "status": "C"
        },
        {
                "lookupId": "639a6b36-8392-4626-8393-174f478716e4",
                "path": "any:participant:relationship",
                "key": "Lessee",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Legal"
                },
                "status": "C"
        },
        {
                "lookupId": "d732077c-ef7c-47db-9a2b-a1093c212ea0",
                "path": "any:participant:relationship",
                "key": "Litigant",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Legal"
                },
                "status": "C"
        },
        {
                "lookupId": "ee25ee3c-09cf-40d0-bcbc-d92e8f4f67ed",
                "path": "any:participant:relationship",
                "key": "Arbitrator",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Legal"
                },
                "status": "C"
        },
        {
                "lookupId": "62d54b47-126a-4e70-994f-83e851e10da9",
                "path": "any:participant:relationship",
                "key": "Mediator",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Legal"
                },
                "status": "C"
        },
        {
                "lookupId": "a9d65aa0-80b1-40cf-a104-a5c864eb4e60",
                "path": "any:participant:relationship",
                "key": "Of Counsel",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Legal"
                },
                "status": "C"
        },
        {
                "lookupId": "0d11edbe-aa6f-4c8a-994e-8bb0e18a7f8c",
                "path": "any:participant:relationship",
                "key": "Case Worker",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Legal"
                },
                "status": "C"
        },
        {
                "lookupId": "a54ae3db-f91f-478c-bfcd-c06b0296494e",
                "path": "any:participant:relationship",
                "key": "Receiver",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Legal"
                },
                "status": "C"
        },
        {
                "lookupId": "b944028b-419d-4176-9d0d-b0a6dccc5af0",
                "path": "any:participant:relationship",
                "key": "Registrar",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Legal"
                },
                "status": "C"
        },
        {
                "lookupId": "7ee81356-76ba-4c44-b763-f2858add2d05",
                "path": "any:participant:relationship",
                "key": "Referee",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Legal"
                },
                "status": "C"
        },
        {
                "lookupId": "81d7d440-0433-4b88-9397-633bae557230",
                "path": "any:participant:relationship",
                "key": "Process Server",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Legal"
                },
                "status": "C"
        },
        {
                "lookupId": "a6318804-1a53-40bf-81bb-c1c99d166ac0",
                "path": "any:participant:relationship",
                "key": "Arresting Officer",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Legal"
                },
                "status": "C"
        },
        {
                "lookupId": "bc508eb7-8e8b-44bf-b7e4-f96a8d5203f4",
                "path": "any:participant:relationship",
                "key": "Husband",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Familial",
                        "opposite": [
                                "499c0925-e590-4d3f-97c2-d72c34cbeb29",
                                "bc508eb7-8e8b-44bf-b7e4-f96a8d5203f4"
                        ]
                },
                "status": "C"
        },
        {
                "lookupId": "499c0925-e590-4d3f-97c2-d72c34cbeb29",
                "path": "any:participant:relationship",
                "key": "Wife",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Familial",
                        "opposite": [
                                "bc508eb7-8e8b-44bf-b7e4-f96a8d5203f4",
                                "499c0925-e590-4d3f-97c2-d72c34cbeb29"
                        ]
                },
                "status": "C"
        },
        {
                "lookupId": "e5b6188e-9f00-4eb7-bad8-9c7627b5fa60",
                "path": "any:participant:relationship",
                "key": "Spouse",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Familial",
                        "opposite": [
                                "e5b6188e-9f00-4eb7-bad8-9c7627b5fa60"
                        ]
                },
                "status": "C"
        },
        {
                "lookupId": "4ef39111-af79-4bc4-b9a9-4a6692c3cefa",
                "path": "any:participant:relationship",
                "key": "Ex-Wife",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Familial",
                        "opposite": [
                                "ced1f331-7e11-4394-a8f2-18c5dfcb19ab",
                                "4ef39111-af79-4bc4-b9a9-4a6692c3cefa"
                        ]
                },
                "status": "C"
        },
        {
                "lookupId": "ced1f331-7e11-4394-a8f2-18c5dfcb19ab",
                "path": "any:participant:relationship",
                "key": "Ex-Husband",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Familial",
                        "opposite": [
                                "4ef39111-af79-4bc4-b9a9-4a6692c3cefa",
                                "ced1f331-7e11-4394-a8f2-18c5dfcb19ab"
                        ]
                },
                "status": "C"
        },
        {
                "lookupId": "c650137a-4ada-46a1-ac6f-b11e7bd0dfea",
                "path": "any:participant:relationship",
                "key": "Ex-Girlfriend",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Familial",
                        "opposite": [
                                "91355001-0d61-4ebd-8ea1-1981ea3e2d14",
                                "c650137a-4ada-46a1-ac6f-b11e7bd0dfea"
                        ]
                },
                "status": "C"
        },
        {
                "lookupId": "91355001-0d61-4ebd-8ea1-1981ea3e2d14",
                "path": "any:participant:relationship",
                "key": "Ex-Boyfriend",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Familial",
                        "opposite": [
                                "c650137a-4ada-46a1-ac6f-b11e7bd0dfea",
                                "91355001-0d61-4ebd-8ea1-1981ea3e2d14"
                        ]
                },
                "status": "C"
        },
        {
                "lookupId": "aae14112-f0f0-4d69-9de5-a4d8072367f3",
                "path": "any:participant:relationship",
                "key": "Baby Mama",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Familial",
                        "opposite": [
                                "127e22bf-834a-46c0-8608-9eb5e135d75e",
                                "4d735ad6-adbb-42b6-9d80-31e343096205"
                        ]
                },
                "status": "C"
        },
        {
                "lookupId": "11baed84-792a-4efa-a5c4-9cea4330d098",
                "path": "any:participant:relationship",
                "key": "Baby Daddy",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Familial",
                        "opposite": [
                                "127e22bf-834a-46c0-8608-9eb5e135d75e",
                                "4d735ad6-adbb-42b6-9d80-31e343096205"
                        ]
                },
                "status": "C"
        },
        {
                "lookupId": "127e22bf-834a-46c0-8608-9eb5e135d75e",
                "path": "any:participant:relationship",
                "key": "Son",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Familial",
                        "opposite": [
                                "4d5a2ad8-4ad8-4ab0-a4e9-5ad03e89d041",
                                "0a694a9c-9545-4a13-8e46-de003fdaa15f"
                        ]
                },
                "status": "C"
        },
        {
                "lookupId": "0579ea6c-8f77-4811-bc42-61212eaffcd6",
                "path": "any:participant:relationship",
                "key": "Son-in-Law",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Familial",
                        "opposite": [
                                "f74c802d-e9b5-4c52-a696-e2be7ee453b5",
                                "ad22f475-ba74-4460-b066-515a28e4a8fc"
                        ]
                },
                "status": "C"
        },
        {
                "lookupId": "4d735ad6-adbb-42b6-9d80-31e343096205",
                "path": "any:participant:relationship",
                "key": "Daughter",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Familial",
                        "opposite": [
                                "4d5a2ad8-4ad8-4ab0-a4e9-5ad03e89d041",
                                "0a694a9c-9545-4a13-8e46-de003fdaa15f"
                        ]
                },
                "status": "C"
        },
        {
                "lookupId": "1a0037c9-eb99-4710-b95d-38c1be8cde9e",
                "path": "any:participant:relationship",
                "key": "Daughter-in-Law",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Familial",
                        "opposite": [
                                "f74c802d-e9b5-4c52-a696-e2be7ee453b5",
                                "ad22f475-ba74-4460-b066-515a28e4a8fc"
                        ]
                },
                "status": "C"
        },
        {
                "lookupId": "35e36f38-6d66-480c-a0be-a6bfbb3eeb78",
                "path": "any:participant:relationship",
                "key": "Brother",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Familial",
                        "opposite": [
                                "35e36f38-6d66-480c-a0be-a6bfbb3eeb78",
                                "8e5d80d8-7f0c-41bf-b2e8-dd2931116a64"
                        ]
                },
                "status": "C"
        },
        {
                "lookupId": "2e0fba37-b304-4d7d-b492-cfb766a1d45f",
                "path": "any:participant:relationship",
                "key": "Brother-in-Law",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Familial",
                        "opposite": [
                                "2e0fba37-b304-4d7d-b492-cfb766a1d45f",
                                "e97d5b41-570e-47fd-9b2a-5556fd075c8d"
                        ]
                },
                "status": "C"
        },
        {
                "lookupId": "8e5d80d8-7f0c-41bf-b2e8-dd2931116a64",
                "path": "any:participant:relationship",
                "key": "Sister",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Familial",
                        "opposite": [
                                "35e36f38-6d66-480c-a0be-a6bfbb3eeb78",
                                "8e5d80d8-7f0c-41bf-b2e8-dd2931116a64"
                        ]
                },
                "status": "C"
        },
        {
                "lookupId": "e97d5b41-570e-47fd-9b2a-5556fd075c8d",
                "path": "any:participant:relationship",
                "key": "Sister-in-Law",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Familial",
                        "opposite": [
                                "2e0fba37-b304-4d7d-b492-cfb766a1d45f",
                                "e97d5b41-570e-47fd-9b2a-5556fd075c8d"
                        ]
                },
                "status": "C"
        },
        {
                "lookupId": "4d5a2ad8-4ad8-4ab0-a4e9-5ad03e89d041",
                "path": "any:participant:relationship",
                "key": "Mother",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Familial",
                        "opposite": [
                                "127e22bf-834a-46c0-8608-9eb5e135d75e",
                                "4d735ad6-adbb-42b6-9d80-31e343096205"
                        ]
                },
                "status": "C"
        },
        {
                "lookupId": "f74c802d-e9b5-4c52-a696-e2be7ee453b5",
                "path": "any:participant:relationship",
                "key": "Mother-in-Law",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Familial",
                        "opposite": [
                                "0579ea6c-8f77-4811-bc42-61212eaffcd6",
                                "1a0037c9-eb99-4710-b95d-38c1be8cde9e"
                        ]
                },
                "status": "C"
        },
        {
                "lookupId": "0a694a9c-9545-4a13-8e46-de003fdaa15f",
                "path": "any:participant:relationship",
                "key": "Father",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Familial",
                        "opposite": [
                                "127e22bf-834a-46c0-8608-9eb5e135d75e",
                                "4d735ad6-adbb-42b6-9d80-31e343096205"
                        ]
                },
                "status": "C"
        },
        {
                "lookupId": "ad22f475-ba74-4460-b066-515a28e4a8fc",
                "path": "any:participant:relationship",
                "key": "Father-in-Law",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Familial",
                        "opposite": [
                                "0579ea6c-8f77-4811-bc42-61212eaffcd6",
                                "1a0037c9-eb99-4710-b95d-38c1be8cde9e"
                        ]
                },
                "status": "C"
        },
        {
                "lookupId": "d44bf143-8d83-4374-bdcb-88e6b6060ee6",
                "path": "any:participant:relationship",
                "key": "Grandmother",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Familial",
                        "opposite": [
                                "0257295a-7e4a-48c8-94b7-e8896cc6732b",
                                "4b05a9f0-efb1-4fe5-9214-c02f5280dc60"
                        ]
                },
                "status": "C"
        },
        {
                "lookupId": "b6c39269-fc33-4cab-924a-e14f213cdf4a",
                "path": "any:participant:relationship",
                "key": "Grandfather",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Familial",
                        "opposite": [
                                "0257295a-7e4a-48c8-94b7-e8896cc6732b",
                                "4b05a9f0-efb1-4fe5-9214-c02f5280dc60"
                        ]
                },
                "status": "C"
        },
        {
                "lookupId": "0257295a-7e4a-48c8-94b7-e8896cc6732b",
                "path": "any:participant:relationship",
                "key": "Grandson",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Familial",
                        "opposite": [
                                "d44bf143-8d83-4374-bdcb-88e6b6060ee6",
                                "b6c39269-fc33-4cab-924a-e14f213cdf4a"
                        ]
                },
                "status": "C"
        },
        {
                "lookupId": "4b05a9f0-efb1-4fe5-9214-c02f5280dc60",
                "path": "any:participant:relationship",
                "key": "Granddaughter",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Familial",
                        "opposite": [
                                "d44bf143-8d83-4374-bdcb-88e6b6060ee6",
                                "b6c39269-fc33-4cab-924a-e14f213cdf4a"
                        ]
                },
                "status": "C"
        },
        {
                "lookupId": "8b6f9748-6c37-4608-a7a9-7239e3a5151c",
                "path": "any:participant:relationship",
                "key": "Cousin",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Familial",
                        "opposite": [
                                "8b6f9748-6c37-4608-a7a9-7239e3a5151c"
                        ]
                },
                "status": "C"
        },
        {
                "lookupId": "3cf1e854-13bc-4d7d-9578-0d06922b81b1",
                "path": "any:participant:relationship",
                "key": "Half-Brother",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Familial",
                        "opposite": [
                                "3cf1e854-13bc-4d7d-9578-0d06922b81b1",
                                "754d506a-815d-41d2-8a97-81a7c4a2ffc9"
                        ]
                },
                "status": "C"
        },
        {
                "lookupId": "754d506a-815d-41d2-8a97-81a7c4a2ffc9",
                "path": "any:participant:relationship",
                "key": "Half-Sister",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Familial",
                        "opposite": [
                                "3cf1e854-13bc-4d7d-9578-0d06922b81b1",
                                "754d506a-815d-41d2-8a97-81a7c4a2ffc9"
                        ]
                },
                "status": "C"
        },
        {
                "lookupId": "bc9054bd-0d23-48c5-b08d-dde943d19242",
                "path": "any:participant:relationship",
                "key": "Stepmother",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Familial",
                        "opposite": [
                                "0fea1eed-356b-41b2-9094-8441a0f92885",
                                "b5e578c2-a73a-47c5-80a6-c1c54ed57051"
                        ]
                },
                "status": "C"
        },
        {
                "lookupId": "0e260da4-fa7f-4633-9bf4-b859bae8f870",
                "path": "any:participant:relationship",
                "key": "Stepfather",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Familial",
                        "opposite": [
                                "0fea1eed-356b-41b2-9094-8441a0f92885",
                                "b5e578c2-a73a-47c5-80a6-c1c54ed57051"
                        ]
                },
                "status": "C"
        },
        {
                "lookupId": "0fea1eed-356b-41b2-9094-8441a0f92885",
                "path": "any:participant:relationship",
                "key": "Stepson",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Familial",
                        "opposite": [
                                "bc9054bd-0d23-48c5-b08d-dde943d19242",
                                "0e260da4-fa7f-4633-9bf4-b859bae8f870"
                        ]
                },
                "status": "C"
        },
        {
                "lookupId": "b5e578c2-a73a-47c5-80a6-c1c54ed57051",
                "path": "any:participant:relationship",
                "key": "Stepdaughter",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Familial",
                        "opposite": [
                                "bc9054bd-0d23-48c5-b08d-dde943d19242",
                                "0e260da4-fa7f-4633-9bf4-b859bae8f870"
                        ]
                },
                "status": "C"
        },
        {
                "lookupId": "6b0ddcaa-9299-49a4-844f-ffaeaedc6ba5",
                "path": "any:participant:relationship",
                "key": "Uncle",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Familial",
                        "opposite": [
                                "1899751d-9717-4be3-8b3d-1117ca097a63",
                                "6d6c0de4-d2bd-4d59-b9ac-549112f083d9"
                        ]
                },
                "status": "C"
        },
        {
                "lookupId": "2fdb44b5-ffe5-48aa-8d9c-5bf8c0bdbe81",
                "path": "any:participant:relationship",
                "key": "Aunt",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Familial",
                        "opposite": [
                                "1899751d-9717-4be3-8b3d-1117ca097a63",
                                "6d6c0de4-d2bd-4d59-b9ac-549112f083d9"
                        ]
                },
                "status": "C"
        },
        {
                "lookupId": "1899751d-9717-4be3-8b3d-1117ca097a63",
                "path": "any:participant:relationship",
                "key": "Niece",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Familial",
                        "opposite": [
                                "6b0ddcaa-9299-49a4-844f-ffaeaedc6ba5",
                                "2fdb44b5-ffe5-48aa-8d9c-5bf8c0bdbe81"
                        ]
                },
                "status": "C"
        },
        {
                "lookupId": "6d6c0de4-d2bd-4d59-b9ac-549112f083d9",
                "path": "any:participant:relationship",
                "key": "Nephew",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Familial",
                        "opposite": [
                                "6b0ddcaa-9299-49a4-844f-ffaeaedc6ba5",
                                "2fdb44b5-ffe5-48aa-8d9c-5bf8c0bdbe81"
                        ]
                },
                "status": "C"
        },
        {
                "lookupId": "4dca2197-f6a2-4b30-9b0b-caf6e265458a",
                "path": "any:participant:relationship",
                "key": "Great-Grandson",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Familial",
                        "opposite": [
                                "a075cd9e-e2ac-44ba-aee4-a43461ee5d78",
                                "05c559cf-9a1c-4a31-9fce-d79fe07e781a"
                        ]
                },
                "status": "C"
        },
        {
                "lookupId": "db9d0eaf-aac2-45a8-b9f1-60b6493f5a78",
                "path": "any:participant:relationship",
                "key": "Great-Granddaughter",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Familial",
                        "opposite": [
                                "a075cd9e-e2ac-44ba-aee4-a43461ee5d78",
                                "05c559cf-9a1c-4a31-9fce-d79fe07e781a"
                        ]
                },
                "status": "C"
        },
        {
                "lookupId": "a075cd9e-e2ac-44ba-aee4-a43461ee5d78",
                "path": "any:participant:relationship",
                "key": "Great-Grandmother",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Familial",
                        "opposite": [
                                "4dca2197-f6a2-4b30-9b0b-caf6e265458a",
                                "db9d0eaf-aac2-45a8-b9f1-60b6493f5a78"
                        ]
                },
                "status": "C"
        },
        {
                "lookupId": "05c559cf-9a1c-4a31-9fce-d79fe07e781a",
                "path": "any:participant:relationship",
                "key": "Great-Grandfather",
                "hasAdditionalData": true,
                "additionalData": {
                        "type": "Familial",
                        "opposite": [
                                "4dca2197-f6a2-4b30-9b0b-caf6e265458a",
                                "db9d0eaf-aac2-45a8-b9f1-60b6493f5a78"
                        ]
                },
                "status": "C"
        },
        {
                "lookupId": "ad1d86b5-5b43-48f2-9cf6-b2101d0d900d",
                "path": "contact:identification:method",
                "key": "Driver's License",
                "status": "C"
        },
        {
                "lookupId": "e1fc3f61-3b0c-46ce-9980-8b0eb5e628ab",
                "path": "contact:identification:method",
                "key": "State ID",
                "status": "C"
        },
        {
                "lookupId": "b335e465-1eaa-41d9-b915-e5ff3a2da063",
                "path": "contact:identification:method",
                "key": "Social Security Number",
                "status": "C"
        },
        {
                "lookupId": "6090d1ce-dc15-4752-9ede-e43b85a341e4",
                "path": "contact:identification:method",
                "key": "Tax Identification Number",
                "status": "C"
        },
        {
                "lookupId": "d03246ee-e3f8-48cd-97af-0eb86a4906c1",
                "path": "contact:identification:method",
                "key": "Military ID",
                "status": "C"
        },
        {
                "lookupId": "f51ef8fc-e983-4e4f-9a50-3b03d936e5df",
                "path": "contact:identification:method",
                "key": "Passport Number",
                "status": "C"
        },
        {
                "lookupId": "1c57bad9-d1a9-46af-a687-421a2e6932a5",
                "path": "contact:identification:method",
                "key": "Birth Certificate",
                "status": "C"
        },
        {
                "lookupId": "f694ce3a-8f5f-40ff-a847-de12e6b44a72",
                "path": "contact:identification:method",
                "key": "Alien Registration Number",
                "status": "C"
        },
        {
                "lookupId": "5bfb2465-bd74-497d-b517-e0dca327dee3",
                "path": "contact:identification:method",
                "key": "USCIS Number",
                "status": "C"
        },
        {
                "lookupId": "0d13cbc7-b0b8-49d6-ba31-63f0d4431e30",
                "path": "contact:identification:method",
                "key": "Bar Number",
                "status": "C"
        },
        {
                "lookupId": "9da6f846-adf4-4f25-8222-57d5c95fe3a0",
                "path": "contact:identification:method",
                "key": "Green Card",
                "status": "C"
        }
].map(e => new Lookup(e));
    }

    /**
     * Get a reference to the schema entity.
     * @static
     * @return {object} - the original schema
     */
    static override getSchema(): EntitySchema {
        return Base.withBaseFields({
            entity: 'lookup',
            table: 'LOOKUP',
            comment: `A centralized catalog of predefined pick-list options used throughout the application. Each row is one selectable option identified by a hierarchical colon-separated path (e.g. 'milestone:reasons:won') and a human-readable key (e.g. 'Personal referral'). Other entities store arrays of lookupId UUIDs to record which options were selected; some entries carry additionalData for extra context such as relationship type or bidirectional opposite-relationship links.`,
            fields: {
                lookupId: {
                    type: 'uuid',
                    required: true,
                    pk: true,
                    default: '__UUID__',
                    comment: 'Unique id for this combination of path and key'
                },
                path: {
                    type: 'string',
                    required: true,
                    comment: 'A colon-separated path, e.g. "milestone:reasons:won"'
                },
                key: {
                    type: 'string',
                    required: true,
                    comment: 'A specific entry in the list of available items for the path, e.g. "money"'
                },
                hasAdditionalData: {
                    type: 'boolean',
                    comment: 'Is there additional data for this lookup?'
                },
                additionalData: {
                    type: 'json',
                    required: false,
                    comment: 'Any additional information about this lookup'
                }
            },
            entries: [
                /* Import */
                {
                    lookupId: '995f186b-a69a-48ea-bba4-9344af4be7a9',
                    path: 'note:tag',
                    key: 'import'
                },
                /* Expense Types */
                /* Legal */
                {
                    lookupId: '442ada96-c371-4701-a021-1a82d619c066',
                    path: 'any:participant:relationship',
                    key: 'Client',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Legal'
                    }
                },
                {
                    lookupId: '73d54d07-2016-4a9e-9c89-ad843d93e178',
                    path: 'any:participant:relationship',
                    key: 'Benefactor',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Legal'
                    }
                },
                {
                    lookupId: 'fa92071d-cf6a-4208-9082-b0a0ca03d85d',
                    path: 'any:participant:relationship',
                    key: 'Assigned Attorney',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Legal'
                    }
                },
                {
                    lookupId: '167b6b1b-9155-4b3c-8063-9a31d8c6c6b3',
                    path: 'any:participant:relationship',
                    key: 'Opposing Counsel',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Legal'
                    }
                },
                {
                    lookupId: '53eb1870-58b4-4f4f-a592-9672d940420b',
                    path: 'any:participant:relationship',
                    key: 'Opposing Party',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Legal'
                    }
                },
                {
                    lookupId: '7a3c50fb-4f96-4741-ad36-363151352422',
                    path: 'any:participant:relationship',
                    key: 'Judge',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Legal'
                    }
                },
                {
                    lookupId: '27ae2710-6fe2-4865-86d4-b571a6be522d',
                    path: 'any:participant:relationship',
                    key: 'Attorney ad Litem',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Legal'
                    }
                },
                {
                    lookupId: '9486b60e-c49d-421e-87dd-cd942f1adf24',
                    path: 'any:participant:relationship',
                    key: 'Guardian ad Litem',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Legal'
                    }
                },
                {
                    lookupId: '4d03aef5-546f-4751-99ca-0f8f9c8ced3a',
                    path: 'any:participant:relationship',
                    key: 'Prosecutor',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Legal'
                    }
                },
                {
                    lookupId: '539e749e-be1c-420e-97e7-58aad5ca38dc',
                    path: 'any:participant:relationship',
                    key: 'Public Defender',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Legal'
                    }
                },
                {
                    lookupId: 'b61d5053-cc57-4325-a670-eec018da935b',
                    path: 'any:participant:relationship',
                    key: 'Plaintiff',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Legal'
                    }
                },
                {
                    lookupId: 'f8aa6e11-70f4-4853-8726-77e62eb22129',
                    path: 'any:participant:relationship',
                    key: 'Defendant',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Legal'
                    }
                },
                {
                    lookupId: 'd69c4e00-7420-4ab5-b646-7b424efa83c5',
                    path: 'any:participant:relationship',
                    key: 'Petitioner',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Legal'
                    }
                },
                {
                    lookupId: 'b7c863ea-9570-4872-bbf0-c46ecfa23fad',
                    path: 'any:participant:relationship',
                    key: 'Respondent',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Legal'
                    }
                },
                {
                    lookupId: 'c4a755e8-d92a-45b3-a405-858157f0ebfb',
                    path: 'any:participant:relationship',
                    key: 'Administrator',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Legal'
                    }
                },
                {
                    lookupId: '5aaf9d59-d315-4346-959a-a0708d2e0c36',
                    path: 'any:participant:relationship',
                    key: 'Executor',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Legal'
                    }
                },
                {
                    lookupId: 'af0badc4-cce2-4b31-b572-fb109e5169b1',
                    path: 'any:participant:relationship',
                    key: 'Appellant',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Legal'
                    }
                },
                {
                    lookupId: '6d5faffc-594e-4bfc-8144-eeab6c5ae22c',
                    path: 'any:participant:relationship',
                    key: 'Appellee',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Legal'
                    }
                },
                {
                    lookupId: '681cd51f-120e-4d38-9238-ceb84a4e5daf',
                    path: 'any:participant:relationship',
                    key: 'Court Clerk',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Legal'
                    }
                },
                {
                    lookupId: '72b5c97a-bab7-4c94-b766-e24394e0b335',
                    path: 'any:participant:relationship',
                    key: 'Court Reporter',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Legal'
                    }
                },
                {
                    lookupId: '0fbb0e71-bac3-4bc5-a91f-b97b027deed6',
                    path: 'any:participant:relationship',
                    key: 'Custodian',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Legal'
                    }
                },
                {
                    lookupId: 'e82e240b-ac9d-4921-a7cc-ae5ecb7d2eb3',
                    path: 'any:participant:relationship',
                    key: 'Guardian',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Legal'
                    }
                },
                {
                    lookupId: 'da0d76fe-d117-4174-a74e-9747fcf17f2b',
                    path: 'any:participant:relationship',
                    key: 'Decedent',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Legal'
                    }
                },
                {
                    lookupId: '23bf7b3e-2d93-4d17-8865-b53a16cbb76d',
                    path: 'any:participant:relationship',
                    key: 'Grantee',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Legal'
                    }
                },
                {
                    lookupId: 'ccfef2fc-36a8-4902-b122-2eb4ae97b69a',
                    path: 'any:participant:relationship',
                    key: 'Grantor',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Legal'
                    }
                },
                {
                    lookupId: '1043b48d-b767-49be-831c-41e5978a7a55',
                    path: 'any:participant:relationship',
                    key: 'Landlord',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Legal'
                    }
                },
                {
                    lookupId: 'cb0f285e-661a-4645-972d-70ccd695bdb8',
                    path: 'any:participant:relationship',
                    key: 'Tenant',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Legal'
                    }
                },
                {
                    lookupId: '639a6b36-8392-4626-8393-174f478716e4',
                    path: 'any:participant:relationship',
                    key: 'Lessee',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Legal'
                    }
                },
                {
                    lookupId: 'd732077c-ef7c-47db-9a2b-a1093c212ea0',
                    path: 'any:participant:relationship',
                    key: 'Litigant',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Legal'
                    }
                },
                {
                    lookupId: 'ee25ee3c-09cf-40d0-bcbc-d92e8f4f67ed',
                    path: 'any:participant:relationship',
                    key: 'Arbitrator',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Legal'
                    }
                },
                {
                    lookupId: '62d54b47-126a-4e70-994f-83e851e10da9',
                    path: 'any:participant:relationship',
                    key: 'Mediator',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Legal'
                    }
                },
                {
                    lookupId: 'a9d65aa0-80b1-40cf-a104-a5c864eb4e60',
                    path: 'any:participant:relationship',
                    key: 'Of Counsel',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Legal'
                    }
                },
                {
                    lookupId: '0d11edbe-aa6f-4c8a-994e-8bb0e18a7f8c',
                    path: 'any:participant:relationship',
                    key: 'Case Worker',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Legal'
                    }
                },
                {
                    lookupId: 'a54ae3db-f91f-478c-bfcd-c06b0296494e',
                    path: 'any:participant:relationship',
                    key: 'Receiver',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Legal'
                    }
                },
                {
                    lookupId: 'b944028b-419d-4176-9d0d-b0a6dccc5af0',
                    path: 'any:participant:relationship',
                    key: 'Registrar',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Legal'
                    }
                },
                {
                    lookupId: '7ee81356-76ba-4c44-b763-f2858add2d05',
                    path: 'any:participant:relationship',
                    key: 'Referee',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Legal'
                    }
                },
                {
                    lookupId: '81d7d440-0433-4b88-9397-633bae557230',
                    path: 'any:participant:relationship',
                    key: 'Process Server',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Legal'
                    }
                },
                {
                    lookupId: 'a6318804-1a53-40bf-81bb-c1c99d166ac0',
                    path: 'any:participant:relationship',
                    key: 'Arresting Officer',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Legal'
                    }
                },
                /* Familial */
                {
                    lookupId: 'bc508eb7-8e8b-44bf-b7e4-f96a8d5203f4',
                    path: 'any:participant:relationship',
                    key: 'Husband',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Familial',
                        opposite: ['499c0925-e590-4d3f-97c2-d72c34cbeb29', 'bc508eb7-8e8b-44bf-b7e4-f96a8d5203f4']
                    }
                },
                {
                    lookupId: '499c0925-e590-4d3f-97c2-d72c34cbeb29',
                    path: 'any:participant:relationship',
                    key: 'Wife',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Familial',
                        opposite: ['bc508eb7-8e8b-44bf-b7e4-f96a8d5203f4', '499c0925-e590-4d3f-97c2-d72c34cbeb29']
                    }
                },
                {
                    lookupId: 'e5b6188e-9f00-4eb7-bad8-9c7627b5fa60',
                    path: 'any:participant:relationship',
                    key: 'Spouse',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Familial',
                        opposite: ['e5b6188e-9f00-4eb7-bad8-9c7627b5fa60']
                    }
                },
                {
                    lookupId: '4ef39111-af79-4bc4-b9a9-4a6692c3cefa',
                    path: 'any:participant:relationship',
                    key: 'Ex-Wife',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Familial',
                        opposite: ['ced1f331-7e11-4394-a8f2-18c5dfcb19ab', '4ef39111-af79-4bc4-b9a9-4a6692c3cefa']
                    }
                },
                {
                    lookupId: 'ced1f331-7e11-4394-a8f2-18c5dfcb19ab',
                    path: 'any:participant:relationship',
                    key: 'Ex-Husband',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Familial',
                        opposite: ['4ef39111-af79-4bc4-b9a9-4a6692c3cefa', 'ced1f331-7e11-4394-a8f2-18c5dfcb19ab']
                    }
                },
                {
                    lookupId: 'c650137a-4ada-46a1-ac6f-b11e7bd0dfea',
                    path: 'any:participant:relationship',
                    key: 'Ex-Girlfriend',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Familial',
                        opposite: ['91355001-0d61-4ebd-8ea1-1981ea3e2d14', 'c650137a-4ada-46a1-ac6f-b11e7bd0dfea']
                    }
                },
                {
                    lookupId: '91355001-0d61-4ebd-8ea1-1981ea3e2d14',
                    path: 'any:participant:relationship',
                    key: 'Ex-Boyfriend',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Familial',
                        opposite: ['c650137a-4ada-46a1-ac6f-b11e7bd0dfea', '91355001-0d61-4ebd-8ea1-1981ea3e2d14']
                    }
                },
                {
                    lookupId: 'aae14112-f0f0-4d69-9de5-a4d8072367f3',
                    path: 'any:participant:relationship',
                    key: 'Baby Mama',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Familial',
                        opposite: ['127e22bf-834a-46c0-8608-9eb5e135d75e', '4d735ad6-adbb-42b6-9d80-31e343096205']
                    }
                },
                {
                    lookupId: '11baed84-792a-4efa-a5c4-9cea4330d098',
                    path: 'any:participant:relationship',
                    key: 'Baby Daddy',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Familial',
                        opposite: ['127e22bf-834a-46c0-8608-9eb5e135d75e', '4d735ad6-adbb-42b6-9d80-31e343096205']
                    }
                },
                {
                    lookupId: '127e22bf-834a-46c0-8608-9eb5e135d75e',
                    path: 'any:participant:relationship',
                    key: 'Son',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Familial',
                        opposite: ['4d5a2ad8-4ad8-4ab0-a4e9-5ad03e89d041', '0a694a9c-9545-4a13-8e46-de003fdaa15f']
                    }
                },
                {
                    lookupId: '0579ea6c-8f77-4811-bc42-61212eaffcd6',
                    path: 'any:participant:relationship',
                    key: 'Son-in-Law',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Familial',
                        opposite: ['f74c802d-e9b5-4c52-a696-e2be7ee453b5', 'ad22f475-ba74-4460-b066-515a28e4a8fc']
                    }
                },
                {
                    lookupId: '4d735ad6-adbb-42b6-9d80-31e343096205',
                    path: 'any:participant:relationship',
                    key: 'Daughter',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Familial',
                        opposite: ['4d5a2ad8-4ad8-4ab0-a4e9-5ad03e89d041', '0a694a9c-9545-4a13-8e46-de003fdaa15f']
                    }
                },
                {
                    lookupId: '1a0037c9-eb99-4710-b95d-38c1be8cde9e',
                    path: 'any:participant:relationship',
                    key: 'Daughter-in-Law',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Familial',
                        opposite: ['f74c802d-e9b5-4c52-a696-e2be7ee453b5', 'ad22f475-ba74-4460-b066-515a28e4a8fc']
                    }
                },
                {
                    lookupId: '35e36f38-6d66-480c-a0be-a6bfbb3eeb78',
                    path: 'any:participant:relationship',
                    key: 'Brother',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Familial',
                        opposite: ['35e36f38-6d66-480c-a0be-a6bfbb3eeb78', '8e5d80d8-7f0c-41bf-b2e8-dd2931116a64']
                    }
                },
                {
                    lookupId: '2e0fba37-b304-4d7d-b492-cfb766a1d45f',
                    path: 'any:participant:relationship',
                    key: 'Brother-in-Law',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Familial',
                        opposite: ['2e0fba37-b304-4d7d-b492-cfb766a1d45f', 'e97d5b41-570e-47fd-9b2a-5556fd075c8d']
                    }
                },
                {
                    lookupId: '8e5d80d8-7f0c-41bf-b2e8-dd2931116a64',
                    path: 'any:participant:relationship',
                    key: 'Sister',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Familial',
                        opposite: ['35e36f38-6d66-480c-a0be-a6bfbb3eeb78', '8e5d80d8-7f0c-41bf-b2e8-dd2931116a64']
                    }
                },
                {
                    lookupId: 'e97d5b41-570e-47fd-9b2a-5556fd075c8d',
                    path: 'any:participant:relationship',
                    key: 'Sister-in-Law',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Familial',
                        opposite: ['2e0fba37-b304-4d7d-b492-cfb766a1d45f', 'e97d5b41-570e-47fd-9b2a-5556fd075c8d']
                    }
                },
                {
                    lookupId: '4d5a2ad8-4ad8-4ab0-a4e9-5ad03e89d041',
                    path: 'any:participant:relationship',
                    key: 'Mother',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Familial',
                        opposite: ['127e22bf-834a-46c0-8608-9eb5e135d75e', '4d735ad6-adbb-42b6-9d80-31e343096205']
                    }
                },
                {
                    lookupId: 'f74c802d-e9b5-4c52-a696-e2be7ee453b5',
                    path: 'any:participant:relationship',
                    key: 'Mother-in-Law',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Familial',
                        opposite: ['0579ea6c-8f77-4811-bc42-61212eaffcd6', '1a0037c9-eb99-4710-b95d-38c1be8cde9e']
                    }
                },
                {
                    lookupId: '0a694a9c-9545-4a13-8e46-de003fdaa15f',
                    path: 'any:participant:relationship',
                    key: 'Father',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Familial',
                        opposite: ['127e22bf-834a-46c0-8608-9eb5e135d75e', '4d735ad6-adbb-42b6-9d80-31e343096205']
                    }
                },
                {
                    lookupId: 'ad22f475-ba74-4460-b066-515a28e4a8fc',
                    path: 'any:participant:relationship',
                    key: 'Father-in-Law',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Familial',
                        opposite: ['0579ea6c-8f77-4811-bc42-61212eaffcd6', '1a0037c9-eb99-4710-b95d-38c1be8cde9e']
                    }
                },
                {
                    lookupId: 'd44bf143-8d83-4374-bdcb-88e6b6060ee6',
                    path: 'any:participant:relationship',
                    key: 'Grandmother',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Familial',
                        opposite: ['0257295a-7e4a-48c8-94b7-e8896cc6732b', '4b05a9f0-efb1-4fe5-9214-c02f5280dc60']
                    }
                },
                {
                    lookupId: 'b6c39269-fc33-4cab-924a-e14f213cdf4a',
                    path: 'any:participant:relationship',
                    key: 'Grandfather',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Familial',
                        opposite: ['0257295a-7e4a-48c8-94b7-e8896cc6732b', '4b05a9f0-efb1-4fe5-9214-c02f5280dc60']
                    }
                },
                {
                    lookupId: '0257295a-7e4a-48c8-94b7-e8896cc6732b',
                    path: 'any:participant:relationship',
                    key: 'Grandson',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Familial',
                        opposite: ['d44bf143-8d83-4374-bdcb-88e6b6060ee6', 'b6c39269-fc33-4cab-924a-e14f213cdf4a']
                    }
                },
                {
                    lookupId: '4b05a9f0-efb1-4fe5-9214-c02f5280dc60',
                    path: 'any:participant:relationship',
                    key: 'Granddaughter',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Familial',
                        opposite: ['d44bf143-8d83-4374-bdcb-88e6b6060ee6', 'b6c39269-fc33-4cab-924a-e14f213cdf4a']
                    }
                },
                {
                    lookupId: '8b6f9748-6c37-4608-a7a9-7239e3a5151c',
                    path: 'any:participant:relationship',
                    key: 'Cousin',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Familial',
                        opposite: ['8b6f9748-6c37-4608-a7a9-7239e3a5151c']
                    }
                },
                {
                    lookupId: '3cf1e854-13bc-4d7d-9578-0d06922b81b1',
                    path: 'any:participant:relationship',
                    key: 'Half-Brother',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Familial',
                        opposite: ['3cf1e854-13bc-4d7d-9578-0d06922b81b1', '754d506a-815d-41d2-8a97-81a7c4a2ffc9']
                    }
                },
                {
                    lookupId: '754d506a-815d-41d2-8a97-81a7c4a2ffc9',
                    path: 'any:participant:relationship',
                    key: 'Half-Sister',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Familial',
                        opposite: ['3cf1e854-13bc-4d7d-9578-0d06922b81b1', '754d506a-815d-41d2-8a97-81a7c4a2ffc9']
                    }
                },
                {
                    lookupId: 'bc9054bd-0d23-48c5-b08d-dde943d19242',
                    path: 'any:participant:relationship',
                    key: 'Stepmother',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Familial',
                        opposite: ['0fea1eed-356b-41b2-9094-8441a0f92885', 'b5e578c2-a73a-47c5-80a6-c1c54ed57051']
                    }
                },
                {
                    lookupId: '0e260da4-fa7f-4633-9bf4-b859bae8f870',
                    path: 'any:participant:relationship',
                    key: 'Stepfather',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Familial',
                        opposite: ['0fea1eed-356b-41b2-9094-8441a0f92885', 'b5e578c2-a73a-47c5-80a6-c1c54ed57051']
                    }
                },
                {
                    lookupId: '0fea1eed-356b-41b2-9094-8441a0f92885',
                    path: 'any:participant:relationship',
                    key: 'Stepson',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Familial',
                        opposite: ['bc9054bd-0d23-48c5-b08d-dde943d19242', '0e260da4-fa7f-4633-9bf4-b859bae8f870']
                    }
                },
                {
                    lookupId: 'b5e578c2-a73a-47c5-80a6-c1c54ed57051',
                    path: 'any:participant:relationship',
                    key: 'Stepdaughter',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Familial',
                        opposite: ['bc9054bd-0d23-48c5-b08d-dde943d19242', '0e260da4-fa7f-4633-9bf4-b859bae8f870']
                    }
                },
                {
                    lookupId: '6b0ddcaa-9299-49a4-844f-ffaeaedc6ba5',
                    path: 'any:participant:relationship',
                    key: 'Uncle',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Familial',
                        opposite: ['1899751d-9717-4be3-8b3d-1117ca097a63', '6d6c0de4-d2bd-4d59-b9ac-549112f083d9']
                    }
                },
                {
                    lookupId: '2fdb44b5-ffe5-48aa-8d9c-5bf8c0bdbe81',
                    path: 'any:participant:relationship',
                    key: 'Aunt',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Familial',
                        opposite: ['1899751d-9717-4be3-8b3d-1117ca097a63', '6d6c0de4-d2bd-4d59-b9ac-549112f083d9']
                    }
                },
                {
                    lookupId: '1899751d-9717-4be3-8b3d-1117ca097a63',
                    path: 'any:participant:relationship',
                    key: 'Niece',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Familial',
                        opposite: ['6b0ddcaa-9299-49a4-844f-ffaeaedc6ba5', '2fdb44b5-ffe5-48aa-8d9c-5bf8c0bdbe81']
                    }
                },
                {
                    lookupId: '6d6c0de4-d2bd-4d59-b9ac-549112f083d9',
                    path: 'any:participant:relationship',
                    key: 'Nephew',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Familial',
                        opposite: ['6b0ddcaa-9299-49a4-844f-ffaeaedc6ba5', '2fdb44b5-ffe5-48aa-8d9c-5bf8c0bdbe81']
                    }
                },
                {
                    lookupId: '4dca2197-f6a2-4b30-9b0b-caf6e265458a',
                    path: 'any:participant:relationship',
                    key: 'Great-Grandson',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Familial',
                        opposite: ['a075cd9e-e2ac-44ba-aee4-a43461ee5d78', '05c559cf-9a1c-4a31-9fce-d79fe07e781a']
                    }
                },
                {
                    lookupId: 'db9d0eaf-aac2-45a8-b9f1-60b6493f5a78',
                    path: 'any:participant:relationship',
                    key: 'Great-Granddaughter',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Familial',
                        opposite: ['a075cd9e-e2ac-44ba-aee4-a43461ee5d78', '05c559cf-9a1c-4a31-9fce-d79fe07e781a']
                    }
                },
                {
                    lookupId: 'a075cd9e-e2ac-44ba-aee4-a43461ee5d78',
                    path: 'any:participant:relationship',
                    key: 'Great-Grandmother',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Familial',
                        opposite: ['4dca2197-f6a2-4b30-9b0b-caf6e265458a', 'db9d0eaf-aac2-45a8-b9f1-60b6493f5a78']
                    }
                },
                {
                    lookupId: '05c559cf-9a1c-4a31-9fce-d79fe07e781a',
                    path: 'any:participant:relationship',
                    key: 'Great-Grandfather',
                    hasAdditionalData: true,
                    additionalData: {
                        type: 'Familial',
                        opposite: ['4dca2197-f6a2-4b30-9b0b-caf6e265458a', 'db9d0eaf-aac2-45a8-b9f1-60b6493f5a78']
                    }
                },
                /* Identification Methods */
                {
                    lookupId: 'ad1d86b5-5b43-48f2-9cf6-b2101d0d900d',
                    path: 'contact:identification:method',
                    key: "Driver's License"
                },
                {
                    lookupId: 'e1fc3f61-3b0c-46ce-9980-8b0eb5e628ab',
                    path: 'contact:identification:method',
                    key: 'State ID'
                },
                {
                    lookupId: 'b335e465-1eaa-41d9-b915-e5ff3a2da063',
                    path: 'contact:identification:method',
                    key: 'Social Security Number'
                },
                {
                    lookupId: '6090d1ce-dc15-4752-9ede-e43b85a341e4',
                    path: 'contact:identification:method',
                    key: 'Tax Identification Number'
                },
                {
                    lookupId: 'd03246ee-e3f8-48cd-97af-0eb86a4906c1',
                    path: 'contact:identification:method',
                    key: 'Military ID'
                },
                {
                    lookupId: 'f51ef8fc-e983-4e4f-9a50-3b03d936e5df',
                    path: 'contact:identification:method',
                    key: 'Passport Number'
                },
                {
                    lookupId: '1c57bad9-d1a9-46af-a687-421a2e6932a5',
                    path: 'contact:identification:method',
                    key: 'Birth Certificate'
                },
                {
                    lookupId: 'f694ce3a-8f5f-40ff-a847-de12e6b44a72',
                    path: 'contact:identification:method',
                    key: 'Alien Registration Number'
                },
                {
                    lookupId: '5bfb2465-bd74-497d-b517-e0dca327dee3',
                    path: 'contact:identification:method',
                    key: 'USCIS Number'
                },
                {
                    lookupId: '0d13cbc7-b0b8-49d6-ba31-63f0d4431e30',
                    path: 'contact:identification:method',
                    key: 'Bar Number'
                },
                {
                    lookupId: '9da6f846-adf4-4f25-8222-57d5c95fe3a0',
                    path: 'contact:identification:method',
                    key: 'Green Card'
                },
            ]
        });
    }

}
export default Lookup;
